import {
  Bot,
  ClipboardCheck,
  Gauge,
  Scale,
  ShieldCheck,
  Target,
} from "lucide-react";
import type {
  GuideSlide,
  GuideStep,
} from "@/components/training/SlideGuide";

export const AI_WORK_DECISION_CARD = `AI WORK DECISION CARD

WORKFLOW
Name the real job or recurring outcome:

TDAR — RESULT FIRST
Business result:
Current baseline, source, period, sample, and guardrail:
AI contribution:
Human contribution:
Re-measure date, owner, and decision rule:

DMP
Big What — the exact decision, boundary, and timing:
Why — result, evidence, urgency, and assumptions:
What — workflow, users, inputs, outputs, boundaries, and finish line:
How — people, AI, data, deterministic checks, effort, and permissions:
What If — uncertainties, alternatives, signals, and stop rules:

RESPONSIBLE AI CONTROLS
Authorized purpose and prohibited uses:
Data, affected people, permissions, and retention:
Tests, thresholds, and evidence retained:
Human review and stop authority:
Incident, change, retirement, and qualified-escalation rules:

RIGHT GEAR
Chat / Assistant / Agents / Loops:
Why this gear fits:

REUSABLE SKILL
Should this become a Skill? Yes / No
Instructions, approved sources, tools, examples, and quality rules it needs:

WORK BOUNDARIES
AI may:
Deterministic software must:
A person must decide or approve:

FINISH LINE
Definition of Done:
Stop rules and exceptions:
Accountable owner:`;

export const LONGHAND_OVERVIEW_STEPS: GuideStep[] = [
  {
    id: "gears",
    title: "Choose the right gear",
    description: "Match the unit of responsibility to Chat, Assistant, Agents, or Loops.",
    duration: "8 min",
    whyItMatters:
      "More autonomy is not automatically better. The useful move is choosing the smallest gear that can own the result without hiding the judgement, risk, or cost.",
    actions: [
      "Start with the unit of work, not the product name or model.",
      "Use Chat for one question and Assistant for one finished deliverable.",
      "Use Agents for a multi-step objective and Loops for a recurring outcome.",
      "Name the direction, review point, and accountable person before shifting up.",
    ],
    checkpoint:
      "You can classify a quick decision question as Chat, a board memo as Assistant, a research-and-draft brief as an Agent, and a weekly priority heartbeat as a Loop — and explain what stays human in each one.",
    icon: Gauge,
    links: [
      {
        label: "Open the Longhand AI Tune-Up",
        href: "/ai-30/program/ai-tune-up",
      },
    ],
  },
  {
    id: "skills",
    title: "Package reusable expertise",
    description: "Build a library of professional procedures instead of one giant agent.",
    duration: "9 min",
    whyItMatters:
      "A general model can reason, but repeatable professional work also needs the procedure, sources, tools, examples, and quality rules a good colleague would learn over time.",
    actions: [
      "Choose one repeated professional task where consistency matters more than novelty.",
      "Package the instructions, approved sources, tools or scripts, good examples, Definition of Done, and stop rules.",
      "Keep large supporting material outside the standing instructions and load it only when the task needs it.",
      "Apply the Colleague Test: another authorized person should be able to run the Skill and recognize a good result.",
    ],
    checkpoint:
      "You can explain why a Skill is reusable procedural knowledge — not a fifth gear — and name one piece of team know-how worth capturing as a Skill.",
    icon: Bot,
    reference: {
      title: "A useful Skill carries the procedure",
      headers: ["Part", "What it holds"],
      rows: [
        ["Instructions", "The sequence, decisions, and boundaries."],
        ["Approved sources", "The evidence and context it may use."],
        ["Tools and scripts", "The reliable actions it can run."],
        ["Good examples", "The quality pattern worth repeating."],
        ["Finish and stop rules", "What done looks like and when to stop."],
      ],
    },
    links: [
      {
        label: "Watch the source video",
        href: "https://www.youtube.com/watch?v=TXiaDHn1dUk",
        external: true,
      },
      {
        label: "Build a Skill in the ELEVATE guide",
        href: "/s2p-elevate-ai-guide#part4",
      },
    ],
  },
  {
    id: "responsible-practice",
    title: "Turn responsible AI into operating controls",
    description: "Use Govern, Map, Measure, and Manage before giving a system access or authority.",
    duration: "12 min",
    whyItMatters:
      "Principles do not control a workflow. A reviewable risk-control record makes the authorized purpose, affected people, permissions, tests, accountable humans, stop rules, and escalation boundaries visible before an AI system can create consequences.",
    actions: [
      "Govern: name the authorized purpose, prohibited secondary uses, accountable humans, applicable rules, change approval, review cadence, and pause or termination authority.",
      "Map: inventory data, sources, sensitivity, retention, affected people, downstream effects, permissions, and every action that is read-only, reversible, approval-gated, external, or prohibited.",
      "Measure: define task-specific quality and safety measures, normal, boundary, adversarial, missing-data, and prohibited-action tests, plus thresholds and evidence to retain before testing begins.",
      "Manage: use layered controls such as minimization, least privilege, deterministic validation, approval gates, monitoring, incident response, regression testing, fallback, and retirement procedures.",
      "Stop and escalate when authority, consent, facts, source currency, policy, jurisdiction, qualified review, or the ability to contest a consequential decision is unclear.",
    ],
    checkpoint:
      "You have a risk-control record that ties every material risk to an operating control or explicit acceptance or escalation decision, an accountable human, evidence, and a stop rule.",
    icon: ShieldCheck,
    prompts: [
      {
        label: "AI risk-control record",
        value: "Create a reviewable AI risk-control record for this workflow. Use five sections: Govern, Map, Measure, Manage, and High-stakes escalation. Include authorized purpose, prohibited secondary uses, accountable humans, applicable policy or explicit unknowns, data/source/authorization/sensitivity/retention, affected people, action permissions, normal/boundary/adversarial/missing-data/prohibited-action tests, predeclared thresholds, human-review criteria, layered controls, monitoring, incident response, change triggers, retirement, stop authority, and qualified review. Separate observed evidence from assumptions. Mark unknown authority or permission as a blocker; do not invent compliance conclusions.\n\nWorkflow: [paste]\nKnown policies and permissions: [paste or say unknown]",
      },
    ],
    deliverables: ["Authorized-purpose and accountability record", "Data, people, and permission map", "Test, control, incident, and escalation plan"],
    note:
      "This practice record does not establish legal compliance, professional approval, or universal safety. High-consequence work still requires the relevant organizational and qualified professional review.",
    reference: {
      title: "Responsible AI as an operating cycle",
      headers: ["Function", "Decision it makes visible", "Minimum evidence"],
      rows: [
        ["Govern", "Why may this exist, who owns it, and who can stop it?", "Purpose, owner, rules, change control, stop authority"],
        ["Map", "What data, people, permissions, and consequences are in scope?", "Data flow, affected parties, actions, authorization"],
        ["Measure", "How will failure be found before reliance?", "Cases, thresholds, reviewer tests, retained results"],
        ["Manage", "Which controls operate, and what happens when they fail or change?", "Controls, monitoring, incident, regression, retirement"],
      ],
    },
    links: [
      { label: "Open the Responsible AI deep dive", href: "/responsible-ai-practice" },
    ],
  },
  {
    id: "tdar",
    title: "Define the return with TDAR",
    description: "Create a measurable result record before choosing a solution.",
    duration: "12 min",
    whyItMatters:
      "An AI initiative can produce impressive activity and no defensible return. TDAR makes the result, comparison point, division of work, and remeasurement decision explicit before implementation creates sunk cost.",
    actions: [
      "Result: name the business or service outcome as a number, rate, time, quality measure, or observable state — not as 'use AI.'",
      "Baseline: record the current value or explicit unknown, source, period, sample, unit, exclusions, and at least one quality guardrail.",
      "AI contribution: state the bounded part of the workflow the system may perform or assist, including authorized inputs and prohibited actions.",
      "Human contribution: state the judgment, verification, approval, relationship, accountability, and exception handling that remain human.",
      "Remeasurement: set the date, sample, owner, comparison method, and continue, revise, and stop rules before the experiment begins.",
    ],
    checkpoint:
      "You saved a five-field TDAR brief another reviewer can inspect without asking what the result, baseline, contribution boundaries, evidence, guardrail, or remeasurement rule means.",
    icon: Target,
    prompts: [
      {
        label: "TDAR Results Brief",
        value: "# TDAR Results Brief\n\n## Workflow and decision\n[What recurring work is being considered, and what decision will this experiment inform?]\n\n## Measurable result\n[Measure, target, unit, population, and time period]\n\n## Baseline\n[Current value or 'unknown'; source; period; sample; exclusions; guardrail]\n\n## Bounded AI contribution\n[Authorized inputs, work performed, outputs, and prohibited actions]\n\n## Accountable human contribution\n[Judgments, reviews, approvals, and owner of consequences]\n\n## Remeasurement\n[Date, sample, comparison method, continue/revise/stop rules]\n\n## Evidence still missing\n[Facts that must be collected or verified]\n\n## Reflection\n[Which assumption is most likely to change the decision?]",
      },
    ],
    deliverables: ["Five-field TDAR Results Brief", "Baseline and quality guardrail", "Dated continue, revise, and stop rules"],
    note:
      "Do not invent a historical baseline. When evidence is unavailable, write 'baseline unknown' and define the collection window required before making a result claim.",
    reference: {
      title: "TDAR field test",
      headers: ["Field", "Review question", "Reject when"],
      rows: [
        ["Result", "What changes for the work or customer?", "It only measures tool use or output volume."],
        ["Baseline", "Compared with what, from which source and period?", "The number is guessed or the conditions are not comparable."],
        ["AI contribution", "What bounded work does the system perform?", "The claim is that AI improves everything."],
        ["Human contribution", "Which judgment and accountability stay human?", "Review is vague or occurs only after harm."],
        ["Remeasurement", "When, on what sample, using what rule?", "Success will be declared after seeing the result."],
      ],
    },
    links: [
      { label: "Open the TDAR deep dive", href: "/tdar-results-formula" },
    ],
  },
  {
    id: "dmp",
    title: "Make the decision with the DMP",
    description: "Expose the choice, evidence, scope, operating model, uncertainty, and alternatives.",
    duration: "12 min",
    whyItMatters:
      "A roadmap item can hide five different disagreements: the actual decision, purpose, scope, execution, and risk. The DMP turns those disagreements into reviewable fields instead of compressing them into a yes or no vote.",
    actions: [
      "Big What: write the consequential decision that must be made now — build, run a bounded test, defer, reject, or choose another path.",
      "Why: state the TDAR result, baseline evidence, urgency, observed facts, assumptions, and what happens if no change is made.",
      "What: define the user, trigger, workflow, authorized inputs, output, downstream consumer, boundaries, non-goals, and finish line.",
      "How: describe the smallest credible operating model, including people, AI surface, data, deterministic checks, permissions, full effort, review, exceptions, and evidence capture.",
      "What If: identify decision-changing uncertainties, early signals, continue, revise, pause, and stop rules, accountable owners, and credible current-process or non-AI alternatives.",
    ],
    checkpoint:
      "You saved a DMP record with a reversible decision, evidence and assumptions separated, bounded scope, a credible operating model, alternatives, uncertainty, stop rules, and a reconsideration trigger.",
    icon: Scale,
    prompts: [
      {
        label: "DMP Decision Record",
        value: "# DMP Decision Record\n\n## Big What\n[Exact decision, boundary, and timing]\n\n## Why\n[TDAR result, baseline evidence, urgency, observed facts, assumptions]\n\n## What\n[User, trigger, workflow, inputs, outputs, finish line, non-goals]\n\n## How\n[People, AI surface, data, deterministic checks, permissions, review, effort, failure handling]\n\n## What If\n[Decision-changing uncertainties, signals, continue/revise/pause/stop rules, owners]\n\n## Alternatives compared\n[Current process, non-AI option, lower-complexity AI option, proposed option]\n\n## Decision and rationale\n[Build, bounded test, defer, reject, or alternative]\n\n## Evidence that would reverse this decision\n[Specific evidence]\n\n## Reconsideration trigger\n[Date or event]",
      },
    ],
    deliverables: ["Five-part DMP decision record", "Alternative comparison", "Decision-reversing evidence and reconsideration trigger"],
    note:
      "The DMP is a Longhand-developed decision record. Its value comes from transparent evidence, explicit alternatives, and a decision another reviewer can challenge — not from making the current recommendation look inevitable.",
    reference: {
      title: "DMP review questions",
      headers: ["Part", "Question", "Common failure"],
      rows: [
        ["Big What", "What decision must be made?", "A topic or aspiration replaces the choice."],
        ["Why", "What result and evidence justify attention?", "Urgency or enthusiasm replaces evidence."],
        ["What", "What exactly is in and out?", "The proposal expands whenever challenged."],
        ["How", "What is the smallest credible operating model?", "The design ignores permissions, people, or review work."],
        ["What If", "What could fail or change the choice?", "Risk is reduced to a generic disclaimer."],
      ],
    },
    links: [
      { label: "Watch the Decision Making Process", href: "https://www.youtube.com/watch?v=NQfKHsbHfnA", external: true },
      { label: "Open the DMP deep dive", href: "/decision-making-process" },
    ],
  },
  {
    id: "boundaries",
    title: "Keep judgement and determinism visible",
    description: "Separate interpretation, exact execution, and accountable decisions.",
    duration: "9 min",
    whyItMatters:
      "AI is probabilistic. Some work benefits from interpretation; other work must produce the same exact result, account for every record, and be safe to rerun. Strong workflows use both deliberately.",
    actions: [
      "Map the front 10%: inputs, context, permissions, and decisions a person supplies before the run.",
      "Give AI the middle 80% where interpretation, research, extraction, drafting, or processing creates leverage.",
      "Map the back 10%: the Definition of Done, review point, exception owner, and feedback that improves the next run.",
      "Use deterministic software when the same inputs must produce the same output and every record must be accounted for.",
      "Let AI suggest an ambiguous mapping; let a person approve it; let deterministic software execute it.",
      "Put review where judgement occurs, with stop rules before an error can spread.",
    ],
    checkpoint:
      "You can place each part of a workflow in the AI, deterministic, or human lane and explain why a final skim alone cannot prove an exact operation was correct.",
    icon: ShieldCheck,
    note:
      "A final review is an approval boundary, not a substitute for schema checks, transactions, idempotency, reconciliation, or other deterministic controls.",
    reference: {
      title: "Three different jobs in one safe system",
      headers: ["AI can help", "Deterministic systems own", "Humans decide"],
      rows: [
        ["Interpret messy headers", "Validate schema and types", "Approve ambiguous mappings"],
        ["Suggest classifications", "Deduplicate with explicit rules", "Resolve exceptions"],
        ["Explain rejected rows", "Import transactionally", "Authorize the real run"],
        ["Draft remediation", "Reconcile exact counts", "Accept business risk"],
      ],
    },
    links: [
      {
        label: "Map a workflow with 10 / 80 / 10",
        href: "/s2p-elevate-ai-guide#ex-3-1",
      },
    ],
  },
  {
    id: "decision-card",
    title: "Complete the AI Work Decision Card",
    description: "Turn the overview into one reviewable decision about real work.",
    duration: "6 min",
    whyItMatters:
      "The overview is useful only when it changes a real decision. The card keeps the result, gear, Skill, controls, review owner, and finish line together on one page.",
    actions: [
      "Complete TDAR: measurable result, defensible baseline, bounded AI contribution, accountable human contribution, and a dated remeasurement rule.",
      "Complete the DMP: Big What, Why, What, How, What If, alternatives, and the evidence that would reverse the decision.",
      "Record the responsible-AI purpose, data and permission boundary, tests, human review, stop authority, incident response, change rule, and qualified escalation.",
      "Choose the gear and reusable Skill, mark the AI, deterministic, and human lanes, then add the Definition of Done and accountable owner.",
    ],
    checkpoint:
      "Another leader can read the completed card and understand why this work matters, how it should run, what must remain exact, and who is accountable for the result.",
    icon: ClipboardCheck,
    prompts: [
      {
        label: "AI Work Decision Card",
        value: AI_WORK_DECISION_CARD,
      },
    ],
  },
];

export const LONGHAND_OVERVIEW_SLIDES: GuideSlide[] = [
  { id: "start", title: "Longhand AI Overview", kind: "cover", tone: "dark" },
  { id: "outcomes", title: "The seven decisions", kind: "outcomes", tone: "paper" },
  { id: "step-gears", title: "Human judgement drives every gear", kind: "custom", tone: "dark", stepId: "gears" },
  { id: "gear-chat", title: "Chat handles one question or idea", kind: "custom", tone: "paper", stepId: "gears" },
  { id: "gear-assistant", title: "Assistant produces one finished deliverable", kind: "custom", tone: "blue", stepId: "gears" },
  { id: "gear-agents", title: "Agents own a multi-step objective", kind: "custom", tone: "paper", stepId: "gears" },
  { id: "gear-loops", title: "Loops keep a recurring outcome moving", kind: "custom", tone: "blue", stepId: "gears" },
  { id: "step-skills", title: "Stop building one giant agent", kind: "custom", tone: "dark", stepId: "skills" },
  { id: "skill-apps", title: "Skills are the apps of the AI era", kind: "custom", tone: "blue", stepId: "skills" },
  { id: "skill-anatomy", title: "Package the procedure a colleague could reuse", kind: "activity", tone: "paper", stepId: "skills", actionIndexes: [1, 2, 3], showWhy: true, showReference: true, showLinks: true },
  { id: "responsible-ai-model", title: "Responsible AI is an operating cycle", kind: "activity", tone: "paper", stepId: "responsible-practice", actionIndexes: [0, 1], showWhy: true, showReference: true },
  { id: "responsible-ai-controls", title: "Tests, controls, and stop rules must operate", kind: "activity", tone: "blue", stepId: "responsible-practice", actionIndexes: [2, 3, 4], showNote: true, showLinks: true },
  { id: "responsible-ai-record", title: "Create the risk-control record", kind: "prompt", tone: "paper", stepId: "responsible-practice", promptIndexes: [0], showDeliverables: true },
  { id: "tdar-model", title: "TDAR starts with the result, not the tool", kind: "activity", tone: "paper", stepId: "tdar", actionIndexes: [0, 1, 2], showWhy: true, showReference: true },
  { id: "tdar-accountability", title: "Separate AI contribution from human accountability", kind: "activity", tone: "blue", stepId: "tdar", actionIndexes: [3, 4], showNote: true, showLinks: true },
  { id: "tdar-record", title: "Create the TDAR Results Brief", kind: "prompt", tone: "paper", stepId: "tdar", promptIndexes: [0], showDeliverables: true },
  { id: "dmp", title: "DMP turns possibility into a decision", kind: "custom", tone: "blue", stepId: "dmp" },
  { id: "dmp-operating-model", title: "Bound the What and make the How credible", kind: "activity", tone: "paper", stepId: "dmp", actionIndexes: [0, 1, 2, 3], showWhy: true, showReference: true },
  { id: "dmp-what-if", title: "What If turns uncertainty into decision rules", kind: "activity", tone: "blue", stepId: "dmp", actionIndexes: [4], showNote: true, showLinks: true },
  { id: "dmp-record", title: "Create the DMP Decision Record", kind: "prompt", tone: "paper", stepId: "dmp", promptIndexes: [0], showDeliverables: true },
  { id: "step-boundaries", title: "Human judgement belongs before, during, and after the run", kind: "custom", tone: "dark", stepId: "boundaries" },
  { id: "when-not-ai", title: "Final review is a boundary, not a magic eraser", kind: "activity", tone: "paper", stepId: "boundaries", actionIndexes: [3, 4, 5], showWhy: true, showNote: true, showReference: true },
  { id: "deterministic-import", title: "Import data on deterministic rails", kind: "custom", tone: "blue", stepId: "boundaries" },
  { id: "step-decision-card", title: "Make one real workflow reviewable", kind: "prompt", tone: "dark", stepId: "decision-card", actionIndexes: [0, 1, 2, 3], promptIndexes: [0], nextLabel: "Complete the overview" },
  { id: "finish", title: "You know what to build — and what not to", kind: "finish", tone: "dark" },
];
